import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Download, Plus } from "lucide-react";
import { PageContainer } from "@/layout/PageContainer";
import { PageHeader } from "@/ui/PageHeader";
import { KPICard } from "@/ui/KPICard";
import { Card, CardBody, SectionHeading } from "@/ui/Card";
import { Badge } from "@/ui/Badge";
import { Avatar } from "@/ui/Avatar";
import { ProgressBar } from "@/ui/ProgressBar";
import { SegmentedFilter, type FilterOption } from "@/ui/SegmentedFilter";
import { Button } from "@/ui/Button";
import { ApiFallbackBanner } from "@/ui/feedback/ApiFallbackBanner";
import { toast } from "@/stores/toast.store";
import { formatDZD, formatUSD } from "@/lib/formatters";
import { WALLET_ALERT_THRESHOLD_USD } from "@/lib/constants";
import { useClients } from "@/features/clients/clients.hooks";
import { useWallets } from "@/features/wallets/wallets.hooks";
import { usePayments } from "@/features/payments/payments.hooks";
import { useDashboardReport } from "./dashboard.hooks";
import { RevenueChart } from "./components/RevenueChart";
import { AdSpendChart } from "./components/AdSpendChart";

type Period = "month" | "3m" | "6m" | "1y";

export function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const PERIODS: FilterOption<Period>[] = [
    { value: "month", label: t("dashboard.period.month") },
    { value: "3m", label: t("dashboard.period.3m") },
    { value: "6m", label: t("dashboard.period.6m") },
    { value: "1y", label: t("dashboard.period.1y") },
  ];
  const [period, setPeriod] = useState<Period>("6m");
  // KPIs from /reports/dashboard (mock fallback when anonymous / API down).
  const { kpis, source, isError, refetch } = useDashboardReport(period);
  const { clients } = useClients();
  const { wallets } = useWallets();
  const { payments } = usePayments();

  const topClients = [...clients].sort((a, b) => b.adSpendUsd - a.adSpendUsd).slice(0, 4);
  const lowWallets = [...wallets].sort((a, b) => a.balanceUsd - b.balanceUsd).slice(0, 6);

  return (
    <PageContainer>
      <PageHeader
        title={t("dashboard.title")}
        subtitle="Aperçu général · Juin 2026"
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={() => toast.info(t("dashboard.export"))}>
              <Download className="h-3.5 w-3.5" /> {t("dashboard.export")}
            </Button>
            <Button size="sm" onClick={() => navigate("/ad-spend")}>
              <Plus className="h-3 w-3" /> {t("dashboard.enterExpense")}
            </Button>
          </>
        }
      />

      <div className="mb-5">
        <SegmentedFilter options={PERIODS} value={period} onChange={setPeriod} />
      </div>

      {source === "mock" && (
        <ApiFallbackBanner
          tone={isError ? "warning" : "info"}
          message={isError ? t("dashboard.banner.apiDown") : t("dashboard.banner.demoMode")}
          actionLabel={isError ? t("common.retry") : t("common.login")}
          onRetry={() => {
            if (isError) refetch();
            else navigate("/login");
          }}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
        <KPICard
          label={t("dashboard.kpi.serviceRevenue")}
          value={formatDZD(kpis.serviceRevenueDzd)}
          accent="linear-gradient(90deg,#22c87a,#1aad65)"
        />
        <KPICard
          label={t("dashboard.kpi.adSpendTotal")}
          value={formatUSD(kpis.adSpendUsd)}
          accent="linear-gradient(90deg,#5B6CF0,#4a5be0)"
        />
        <KPICard
          label={t("dashboard.kpi.walletBalance")}
          value={formatUSD(kpis.walletBalanceUsd)}
          accent="linear-gradient(90deg,#f0a030,#d4891a)"
          note={kpis.lowWallets > 0 ? <span className="text-warning">⚠ {t("dashboard.walletsAlert", { count: kpis.lowWallets })}</span> : undefined}
        />
        <KPICard
          label={t("dashboard.kpi.netProfit")}
          value={formatDZD(kpis.netProfitDzd)}
          accent="linear-gradient(90deg,#9b72f0,#7d50d4)"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-4 mb-4">
        <Card>
          <CardBody>
            <SectionHeading
              title={t("dashboard.sections.revenueProfit")}
              subtitle="6 derniers mois · DZD"
              action={<Badge>Avr 2026</Badge>}
            />
            <RevenueChart />
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <SectionHeading title={t("dashboard.sections.adSpend")} />
            <AdSpendChart />
          </CardBody>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card>
          <CardBody>
            <SectionHeading
              title={t("dashboard.sections.topClients")}
              action={
                <button className="text-[11px] text-primary hover:underline" onClick={() => navigate("/clients")}>
                  {t("common.viewAll")}
                </button>
              }
            />
            <div className="space-y-1">
              {topClients.map((c) => (
                <div key={c.id} className="flex items-center gap-3 py-2">
                  <Avatar name={c.name} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold text-text truncate">{c.name}</div>
                    <div className="text-[11px] text-text2">{formatUSD(c.adSpendUsd)} pub</div>
                  </div>
                  <Badge tone={c.status === "ACTIVE" ? "success" : c.status === "PAUSED" ? "warning" : "info"}>
                    {c.status === "ACTIVE" ? t("clients.status.active") : c.status === "PAUSED" ? t("clients.status.paused") : t("clients.status.onboarding")}
                  </Badge>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <SectionHeading
              title={t("dashboard.sections.wallets")}
              action={
                <button className="text-[11px] text-primary hover:underline" onClick={() => navigate("/wallets")}>
                  {t("common.viewAll")}
                </button>
              }
            />
            <div className="space-y-2.5">
              {lowWallets.map((w) => {
                const tone = w.balanceUsd < 0 ? "danger" : w.balanceUsd < WALLET_ALERT_THRESHOLD_USD ? "warning" : "primary";
                const pct = Math.max(4, Math.min(100, (w.balanceUsd / 200) * 100));
                return (
                  <div key={w.id}>
                    <div className="flex justify-between text-[11px] mb-1">
                      <span className="text-text2 truncate">{w.clientName}</span>
                      <span className={`mono ${w.balanceUsd < 0 ? "text-danger" : "text-text"}`}>
                        {formatUSD(w.balanceUsd)}
                      </span>
                    </div>
                    <ProgressBar value={pct} tone={tone} />
                  </div>
                );
              })}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <SectionHeading title={t("dashboard.sections.recentActivity")} />
            <div className="space-y-1">
              {payments.slice(0, 5).map((p) => (
                <div key={p.id} className="flex items-start gap-2.5 py-2 border-b border-border last:border-0">
                  <div className="flex items-center justify-center h-7 w-7 rounded-lg bg-success-dim text-success text-xs shrink-0">
                    ✓
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-semibold text-text">{t("dashboard.payment")} · {p.clientName}</div>
                    <div className="mono text-[11px] text-primary mt-0.5">{formatDZD(p.amount)}</div>
                  </div>
                  <span className="text-[10px] text-text3 shrink-0">{p.date}</span>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      </div>
    </PageContainer>
  );
}
